import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select,
  DatePicker, Switch, message, Popconfirm, Descriptions, InputNumber,
  Typography, Divider, Badge, Alert
} from 'antd';
import {
  CalendarOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined, ClockCircleOutlined,
  UploadOutlined, DownloadOutlined
} from '@ant-design/icons';
import { activityAPI } from '../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// 各活动类型的默认XML模板
const DEFAULT_XML_TEMPLATES = {
  3037: `<activity>
  <base_image>
    <item bg="build_bg" bg1="" bg2="" icon="build_icon" banner="build_banner" banner_shop="build_shop" banner_buy="build_buy" rank_min="10" ratio="10"/>
  </base_image>
  <tasks>
    <item id="1" type="1" detail="任务描述" requirement="100" jump="1" rewards="1,1000" taskPage="1" target_id="0"/>
  </tasks>
  <taskPage_rewards>
    <item taskPage="1" page_rewards="2,5000"/>
  </taskPage_rewards>
  <shopBuy>
    <item id="1" item="3,1" limitBuy="10" price="4,100" limitScore="0" pageShow="1" newItem="0"/>
  </shopBuy>
  <match_groups>
    <item group="1" server="1,2,3"/>
  </match_groups>
  <personal_rewards>
    <item personalRankingInd="1,10" personalRankingReward="1,10000"/>
  </personal_rewards>
  <score_rewards>
    <item score="100" scoreReward="1,5000"/>
  </score_rewards>
</activity>`,

  1015: `<activity>
  <base_image>
    <item bg="turntable_bg" bg1="" bg2="" icon="turntable_icon" banner="turntable_banner"/>
  </base_image>
  <lottery>
    <item id="1" reward="1,1000" weight="100"/>
  </lottery>
  <cost>
    <item cost="4,100"/>
  </cost>
</activity>`,

  1013: `<activity>
  <base_image>
    <item bg="exchange_bg" bg1="" bg2="" icon="exchange_icon" banner="exchange_banner"/>
  </base_image>
  <exchange>
    <item id="1" cost="4,100" reward="1,1000" limit="10"/>
  </exchange>
</activity>`,

  1012: `<activity>
  <base_image>
    <item bg="lotto_bg" bg1="" bg2="" icon="lotto_icon" banner="lotto_banner"/>
  </base_image>
  <lottery>
    <item id="1" reward="1,1000" weight="100"/>
  </lottery>
  <cost>
    <item cost="4,100"/>
  </cost>
</activity>`,

  3024: `<activity>
  <base_image>
    <item bg="festival_bg" bg1="" bg2="" icon="festival_icon" banner="festival_banner"/>
  </base_image>
  <tasks>
    <item id="1" type="1" detail="任务描述" requirement="100" jump="1" rewards="1,1000"/>
  </tasks>
</activity>`,

  1010: `<activity>
  <base_image>
    <item bg="payment_bg" bg1="" bg2="" icon="payment_icon" banner="payment_banner"/>
  </base_image>
  <payment>
    <item id="1" amount="100" reward="1,1000"/>
  </payment>
</activity>`,

  1004: `<activity>
  <base_image>
    <item bg="mine_bg" bg1="" bg2="" icon="mine_icon" banner="mine_banner"/>
  </base_image>
  <buff>
    <item buffId="1" ratio="10"/>
  </buff>
</activity>`,

  1005: `<activity>
  <base_image>
    <item bg="mapmine_bg" bg1="" bg2="" icon="mapmine_icon" banner="mapmine_banner"/>
  </base_image>
  <buff>
    <item buffId="1" ratio="10"/>
  </buff>
</activity>`,

  1006: `<activity>
  <base_image>
    <item bg="killmonster_bg" bg1="" bg2="" icon="killmonster_icon" banner="killmonster_banner"/>
  </base_image>
  <buff>
    <item buffId="1" ratio="10"/>
  </buff>
</activity>`,

  1027: `<activity>
  <base_image>
    <item bg="openServer_bg" bg1="" bg2="" icon="openServer_icon" banner="openServer_banner"/>
  </base_image>
  <tasks>
    <item id="1" type="1" detail="任务描述" requirement="100" jump="1" rewards="1,1000"/>
  </tasks>
</activity>`,

  1002: `<activity>
  <base_image>
    <item bg="cycle_bg" bg1="" bg2="" icon="cycle_icon" banner="cycle_banner"/>
  </base_image>
  <cycle>
    <item id="1" day="1" reward="1,1000"/>
  </cycle>
</activity>`,

  3001: `<activity>
  <base_image>
    <item bg="worldboss_bg" bg1="" bg2="" icon="worldboss_icon" banner="worldboss_banner"/>
  </base_image>
  <boss>
    <item id="1" monsterId="1001" hp="1000000"/>
  </boss>
  <rank_rewards>
    <item rank="1,10" reward="1,10000"/>
  </rank_rewards>
</activity>`,

  1066: `<activity>
  <base_image>
    <item bg="realm_bg" bg1="" bg2="" icon="realm_icon" banner="realm_banner"/>
  </base_image>
  <tasks>
    <item id="1" type="1" detail="任务描述" requirement="100" jump="1" rewards="1,1000"/>
  </tasks>
</activity>`,

  1087: `<activity>
  <base_image>
    <item bg="supercycle_bg" bg1="" bg2="" icon="supercycle_icon" banner="supercycle_banner"/>
  </base_image>
  <cycle>
    <item id="1" day="1" reward="1,1000"/>
  </cycle>
</activity>`,

  3015: `<activity>
  <base_image>
    <item bg="dungeon_bg" bg1="" bg2="" icon="dungeon_icon" banner="dungeon_banner"/>
  </base_image>
  <dungeon>
    <item id="1" floor="1" reward="1,1000"/>
  </dungeon>
</activity>`,

  3020: `<activity>
  <base_image>
    <item bg="team_bg" bg1="" bg2="" icon="team_icon" banner="team_banner"/>
  </base_image>
  <tasks>
    <item id="1" type="1" detail="任务描述" requirement="100" jump="1" rewards="1,1000"/>
  </tasks>
</activity>`,

  3019: `<activity>
  <base_image>
    <item bg="bounty_bg" bg1="" bg2="" icon="bounty_icon" banner="bounty_banner"/>
  </base_image>
  <bounty>
    <item id="1" reward="1,1000" cost="4,100"/>
  </bounty>
</activity>`,

  1017: `<activity>
  <base_image>
    <item bg="text_bg" bg1="" bg2="" icon="text_icon" banner="text_banner"/>
  </base_image>
  <text>
    <item id="1" content="活动文本内容"/>
  </text>
</activity>`
};

// 字段名中英文映射
const FIELD_NAME_MAP = {
  id: 'ID',
  type: '类型',
  requirement: '需求',
  icon: '图标',
  location: '位置',
  jump: '跳转',
  rewards: '奖励',
  reward: '奖励',
  taskPage: '任务页',
  target_id: '目标ID',
  detail: '详情',
  bg: '背景',
  bg1: '背景1',
  bg2: '背景2',
  banner: '横幅',
  banner_shop: '商店横幅',
  banner_buy: '购买横幅',
  rewardvalue: '奖励值',
  theme: '主题',
  is_open_server: '是否开服',
  open_start_day: '开服开始天数',
  open_act_valid_day: '活动有效天数',
  EndTime: '结束时间',
  TakeEndTime: '领取结束时间',
  collect_reward: '收集奖励',
  task_reward: '任务奖励',
  duration_day: '持续天数',
  server_day: '服务器天数',
  actDuration: '活动持续时间',
  serverOpenDuration: '服务器开放持续时间',
  minCityLevel: '最低城市等级',
  popMinCityLv: '弹窗最低城市等级',
  hide: '隐藏',
  minCityLv: '最低城市等级',
  durationDays: '持续天数',
  rank_min: '最低排名',
  ratio: '比例',
  page_rewards: '页面奖励',
  personalRankingReward: '个人排名奖励',
  scoreReward: '分数奖励',
  item: '物品',
  cost: '消耗',
  limitBuy: '限购数量',
  price: '价格',
  limitScore: '限制分数',
  pageShow: '页面显示',
  newItem: '新物品',
  group: '组',
  server: '服务器',
  personalRankingInd: '个人排名索引',
  score: '分数',
  content: '内容',
  amount: '金额',
  buffId: '增益ID',
  day: '天数',
  monsterId: '怪物ID',
  hp: '生命值',
  floor: '楼层',
  rank: '排名',
  weight: '权重'
};

// 节点名中英文映射
const SECTION_NAME_MAP = {
  base_image: '基础图片',
  tasks: '任务',
  taskPage_rewards: '任务页奖励',
  shopBuy: '商店购买',
  match_groups: '匹配组',
  personal_rewards: '个人奖励',
  score_rewards: '分数奖励',
  text: '文本',
  lottery: '抽奖',
  cost: '消耗',
  exchange: '兑换',
  payment: '支付',
  buff: '增益',
  cycle: '周期',
  boss: 'Boss',
  rank_rewards: '排名奖励',
  dungeon: '地牢',
  bounty: '悬赏'
};

// 各活动类型的字段映射
const ACTIVITY_TYPE_FIELD_MAPS = {
  // 建筑活动 (3037)
  3037: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅',
      banner_shop: '商店横幅', banner_buy: '购买横幅', rank_min: '最低排名', ratio: '比例'
    },
    tasks: {
      id: 'ID', type: '类型', detail: '详情', requirement: '需求', jump: '跳转',
      rewards: '奖励', taskPage: '任务页', target_id: '目标ID'
    },
    taskPage_rewards: {
      taskPage: '任务页', page_rewards: '页面奖励'
    },
    shopBuy: {
      id: 'ID', item: '物品', limitBuy: '限购数量', price: '价格',
      limitScore: '限制分数', pageShow: '页面显示', newItem: '新物品'
    },
    match_groups: {
      group: '组', server: '服务器'
    },
    personal_rewards: {
      personalRankingInd: '个人排名索引', personalRankingReward: '个人排名奖励'
    },
    score_rewards: {
      score: '分数', scoreReward: '分数奖励'
    }
  },
  // 转盘活动 (1015, 1012)
  1015: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    lottery: {
      id: 'ID', reward: '奖励', weight: '权重'
    },
    cost: {
      cost: '消耗'
    }
  },
  1012: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    lottery: {
      id: 'ID', reward: '奖励', weight: '权重'
    },
    cost: {
      cost: '消耗'
    }
  },
  // 兑换活动 (1013)
  1013: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    exchange: {
      id: 'ID', cost: '消耗', reward: '奖励', limit: '限制'
    }
  },
  // 节日活动 (3024)
  3024: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    tasks: {
      id: 'ID', type: '类型', detail: '详情', requirement: '需求', jump: '跳转', rewards: '奖励'
    }
  },
  // 充值活动 (1010)
  1010: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    payment: {
      id: 'ID', amount: '金额', reward: '奖励'
    }
  },
  // 挖矿活动 (1004, 1005, 1006)
  1004: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    buff: {
      buffId: '增益ID', ratio: '比例'
    }
  },
  1005: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    buff: {
      buffId: '增益ID', ratio: '比例'
    }
  },
  1006: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    buff: {
      buffId: '增益ID', ratio: '比例'
    }
  },
  // 开服活动 (1027)
  1027: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    tasks: {
      id: 'ID', type: '类型', detail: '详情', requirement: '需求', jump: '跳转', rewards: '奖励'
    }
  },
  // 周期活动 (1002, 1087)
  1002: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    cycle: {
      id: 'ID', day: '天数', reward: '奖励'
    }
  },
  1087: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    cycle: {
      id: 'ID', day: '天数', reward: '奖励'
    }
  },
  // 世界Boss (3001)
  3001: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    boss: {
      id: 'ID', monsterId: '怪物ID', hp: '生命值'
    },
    rank_rewards: {
      rank: '排名', reward: '奖励'
    }
  },
  // 秘境活动 (1066)
  1066: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    tasks: {
      id: 'ID', type: '类型', detail: '详情', requirement: '需求', jump: '跳转', rewards: '奖励'
    }
  },
  // 地牢活动 (3015)
  3015: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    dungeon: {
      id: 'ID', floor: '楼层', reward: '奖励'
    }
  },
  // 组队活动 (3020)
  3020: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    tasks: {
      id: 'ID', type: '类型', detail: '详情', requirement: '需求', jump: '跳转', rewards: '奖励'
    }
  },
  // 悬赏活动 (3019)
  3019: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    bounty: {
      id: 'ID', reward: '奖励', cost: '消耗'
    }
  },
  // 文本活动 (1017)
  1017: {
    base_image: {
      bg: '背景', bg1: '背景1', bg2: '背景2', icon: '图标', banner: '横幅'
    },
    text: {
      id: 'ID', content: '内容'
    }
  }
};

// 解析XML中的物品信息为可编辑的数据结构
function parseXmlToEditable(xmlString, activityType = null) {
  if (!xmlString || !xmlString.trim()) {
    return { sections: [], xmlString: '' };
  }
  
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const root = xmlDoc.documentElement;
    
    if (!root || root.nodeName === 'parsererror') {
      console.error('[XML解析] 解析错误:', xmlString.substring(0, 200));
      return { sections: [], xmlString };
    }
    
    const sections = [];
    
    console.log('[XML解析] 根节点:', root.nodeName, '子节点数:', root.children.length);
    
    // 遍历所有节点
    for (const section of root.children) {
      const sectionName = section.nodeName;
      const rows = [];
      
      console.log('[XML解析] 处理节点:', sectionName, '子元素数:', section.children.length);
      
      for (const node of section.children) {
        const rowData = { key: rows.length };
        
        // 处理 row_data 格式（子元素格式）
        if (node.nodeName === 'row_data') {
          for (const child of node.children) {
            rowData[child.nodeName] = child.textContent;
          }
        } 
        // 处理 item 格式（属性格式）
        else if (node.nodeName === 'item') {
          for (const attr of node.attributes) {
            rowData[attr.name] = attr.value;
          }
        }
        // 处理其他格式（通用处理）
        else {
          // 尝试获取属性
          for (const attr of node.attributes) {
            rowData[attr.name] = attr.value;
          }
          // 尝试获取子元素
          for (const child of node.children) {
            rowData[child.nodeName] = child.textContent;
          }
        }
        
        // 只添加有数据的行
        if (Object.keys(rowData).length > 1) {
          rows.push(rowData);
        }
      }
      
      console.log('[XML解析] 节点', sectionName, '解析行数:', rows.length);
      
      if (rows.length > 0) {
        sections.push({
          name: SECTION_NAME_MAP[sectionName] || sectionName,
          originalName: sectionName,
          rows
        });
      }
    }
    
    console.log('[XML解析] 最终解析结果:', sections.length, '个节点');
    
    return { sections, xmlString };
  } catch (error) {
    console.error('[XML解析] 错误:', error);
    return { sections: [], xmlString };
  }
}

// 将编辑后的数据转换回XML
function editableToXml(sections, originalXml) {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(originalXml, 'text/xml');
    const root = xmlDoc.documentElement;
    
    if (!root || root.nodeName === 'parsererror') {
      return originalXml;
    }
    
    // 遍历每个section
    for (const sectionData of sections) {
      // 查找原始节点名（可能是英文）
      const originalSectionName = Object.keys(SECTION_NAME_MAP).find(key => SECTION_NAME_MAP[key] === sectionData.name) || sectionData.name;
      const section = root.querySelector(originalSectionName);
      if (!section) continue;
      
      // 检查是 row_data 格式还是 item 格式
      const rowNodes = section.querySelectorAll('row_data');
      const itemNodes = section.querySelectorAll('item');
      
      sectionData.rows.forEach((rowData, index) => {
        // 处理 row_data 格式
        if (rowNodes.length > 0 && index < rowNodes.length) {
          const rowNode = rowNodes[index];
          for (const [key, value] of Object.entries(rowData)) {
            if (key === 'key') continue;
            const field = rowNode.querySelector(key);
            if (field) {
              field.textContent = value;
            }
          }
        }
        // 处理 item 格式
        else if (itemNodes.length > 0 && index < itemNodes.length) {
          const itemNode = itemNodes[index];
          for (const [key, value] of Object.entries(rowData)) {
            if (key === 'key') continue;
            itemNode.setAttribute(key, value);
          }
        }
      });
    }
    
    const serializer = new XMLSerializer();
    return serializer.serializeToString(xmlDoc);
  } catch (error) {
    console.error('XML生成错误:', error);
    return originalXml;
  }
}

// 解析XML中的物品信息（只读显示用）
function parseXmlItems(xmlString, activityType = null) {
  if (!xmlString) {
    return [];
  }
  
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const root = xmlDoc.documentElement;
    
    if (!root || root.nodeName === 'parsererror') {
      return [];
    }
    
    const items = [];
    let keyCounter = 1;
    
    // 获取活动类型的字段映射
    const typeFieldMap = activityType ? ACTIVITY_TYPE_FIELD_MAPS[activityType] : null;
    
    // 遍历所有节点
    for (const section of root.children) {
      const sectionName = section.nodeName;
      const sectionFieldMap = typeFieldMap?.[sectionName];
      
      for (const node of section.children) {
        // 检查是否是 row_data 格式（子元素格式）
        if (node.nodeName === 'row_data') {
          // 查找物品相关字段
          const itemFields = ['rewards', 'reward', 'item', 'page_rewards', 
                             'personalRankingReward', 'scoreReward', 'collect_reward', 'task_reward'];
          
          for (const field of itemFields) {
            const fieldElement = node.querySelector(field);
            if (fieldElement && fieldElement.textContent) {
              const value = fieldElement.textContent.trim();
              
              // 处理分号分隔的多个物品（如：10001,10;10002,1）
              if (value.includes(';')) {
                const parts = value.split(';');
                for (const part of parts) {
                  const itemParts = part.split(',');
                  if (itemParts.length >= 2) {
                    const hasNumber = itemParts.some(p => !isNaN(parseInt(p)) && p !== '');
                    if (hasNumber) {
                      items.push({
                        key: keyCounter++,
                        section: SECTION_NAME_MAP[sectionName] || sectionName,
                        node_id: node.querySelector('id')?.textContent || '',
                        attr_name: sectionFieldMap?.[field] || FIELD_NAME_MAP[field] || field,
                        item_id: itemParts[0],
                        count: itemParts.slice(1).join(','),
                        full_value: part
                      });
                    }
                  }
                }
              } else if (value.includes(',')) {
                // 处理逗号分隔的物品（如：67240092,1,100）
                const itemParts = value.split(',');
                if (itemParts.length >= 2) {
                  const hasNumber = itemParts.some(p => !isNaN(parseInt(p)) && p !== '');
                  if (hasNumber) {
                    items.push({
                      key: keyCounter++,
                      section: SECTION_NAME_MAP[sectionName] || sectionName,
                      node_id: node.querySelector('id')?.textContent || '',
                      attr_name: sectionFieldMap?.[field] || FIELD_NAME_MAP[field] || field,
                      item_id: itemParts[0],
                      count: itemParts.slice(1).join(','),
                      full_value: value
                    });
                  }
                }
              }
            }
          }
        } else {
          // 属性格式（原有逻辑）
          const nodeId = node.getAttribute('id') || '';
          
          const itemAttrs = ['rewards', 'reward', 'page_rewards', 'personalRankingReward', 
                            'scoreReward', 'item'];
          
          for (const attr of itemAttrs) {
            const value = node.getAttribute(attr);
            if (value) {
              if (value.includes(',')) {
                const parts = value.split(',');
                if (parts.length >= 2) {
                  const hasNumber = parts.some(p => !isNaN(parseInt(p)) && p !== '');
                  if (hasNumber) {
                    items.push({
                      key: keyCounter++,
                      section: SECTION_NAME_MAP[sectionName] || sectionName,
                      node_id: nodeId,
                      attr_name: sectionFieldMap?.[attr] || FIELD_NAME_MAP[attr] || attr,
                      item_id: parts[0],
                      count: parts[1],
                      full_value: value
                    });
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return items;
  } catch (error) {
    console.error('[XML解析] 错误:', error);
    return [];
  }
}

const STATUS_MAP = {
  0: { text: '草稿', color: 'default' },
  1: { text: '已审核', color: 'green' },
  2: { text: '已拒绝', color: 'red' }
};

const ACTIVITY_STATUS_MAP = {
  0: { text: '未开始', color: 'default' },
  1: { text: '进行中', color: 'processing' },
  2: { text: '已结束', color: 'error' }
};

function getActivityStatus(record) {
  const now = Date.now() / 1000;
  if (now < record.begin_time / 1000) return 0;
  if (now > record.end_time / 1000) return 2;
  return 1;
}

function ActivityManagement() {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [detailActivity, setDetailActivity] = useState(null);
  const [xmlContent, setXmlContent] = useState('');
  const [editableXmlData, setEditableXmlData] = useState({ sections: [], xmlString: '' });
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const [, setFilters] = useState({});
  const [activityTypes, setActivityTypes] = useState([]);
  const { current: pageCurrent, pageSize } = pagination;

  const loadActivities = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await activityAPI.getActivities({
        page: pageCurrent,
        limit: pageSize,
        ...params
      });
      if (response.data.success) {
        const data = response.data.data;
        const list = Array.isArray(data) ? data : (data.activities || data.list || data.records || []);
        const total = Array.isArray(data) ? data.length : (data.pagination?.total || data.total || 0);
        setActivities(list);
        setPagination(prev => ({ ...prev, total }));
      }
    } catch (error) {
      console.error('加载活动列表失败:', error);
      message.error('加载活动列表失败');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [pageCurrent, pageSize]);

  const loadActivityTypes = useCallback(async () => {
    try {
      const response = await activityAPI.getActivityTypes();
      if (response.data.success) {
        setActivityTypes(response.data.data);
      }
    } catch (error) {
      console.error('加载活动类型失败:', error);
    }
  }, []);

  useEffect(() => {
    loadActivities();
    loadActivityTypes();
  }, [loadActivities, loadActivityTypes]);

  // 活动类型选择变化时自动填充XML模板
  const handleTypeChange = (type) => {
    if (!editingActivity && type && DEFAULT_XML_TEMPLATES[type]) {
      setXmlContent(DEFAULT_XML_TEMPLATES[type]);
    }
  };

  const handleCreate = () => {
    setEditingActivity(null);
    setXmlContent('');
    setEditableXmlData({ sections: [], xmlString: '' });
    form.resetFields();
    form.setFieldsValue({
      status: 1,
      enable: 1,
      visible: 1,
      is_notice: 0,
      recommend: 0,
      preview_time: 0,
      endview_time: 0,
      ind: 0
    });
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      // 先获取活动详情，确保有完整的xml_data
      const response = await activityAPI.getActivity(record.id);
      if (response.data.success) {
        const fullRecord = response.data.data;
        setEditingActivity(fullRecord);
        setXmlContent(fullRecord.xml_data || '');
        setEditableXmlData(parseXmlToEditable(fullRecord.xml_data || ''));
        form.setFieldsValue({
          ...fullRecord,
          time_range: [
            dayjs(fullRecord.begin_time),
            dayjs(fullRecord.end_time)
          ],
          recommend_time_range: fullRecord.recommend_begin_time && fullRecord.recommend_end_time ? [
            dayjs(fullRecord.recommend_begin_time),
            dayjs(fullRecord.recommend_end_time)
          ] : null
        });
        setModalVisible(true);
      }
    } catch (error) {
      message.error('加载活动详情失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = { ...values };
      if (values.time_range) {
        data.begin_time = values.time_range[0].toDate().toISOString();
        data.end_time = values.time_range[1].toDate().toISOString();
      }
      if (values.recommend_time_range) {
        data.recommend_begin_time = values.recommend_time_range[0].toDate().toISOString();
        data.recommend_end_time = values.recommend_time_range[1].toDate().toISOString();
      }
      
      // 如果有可编辑的XML数据，转换回XML字符串
      if (editableXmlData.sections.length > 0) {
        data.xml_data = editableToXml(editableXmlData.sections, editableXmlData.xmlString);
      } else {
        data.xml_data = xmlContent;
      }
      
      delete data.time_range;
      delete data.recommend_time_range;

      if (editingActivity) {
        await activityAPI.updateActivity(editingActivity.id, data);
        message.success('活动更新成功');
      } else {
        await activityAPI.createActivity(data);
        message.success('活动创建成功');
      }
      setModalVisible(false);
      loadActivities();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await activityAPI.deleteActivity(id);
      message.success('活动删除成功');
      loadActivities();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await activityAPI.getActivity(record.id);
      if (response.data.success) {
        setDetailActivity(response.data.data);
        setDetailVisible(true);
      }
    } catch (error) {
      message.error('加载活动详情失败');
    }
  };

  const handleDownloadXml = () => {
    if (!detailActivity || !detailActivity.xml_data) {
      message.warning('没有XML配置数据');
      return;
    }
    const blob = new Blob([detailActivity.xml_data], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${detailActivity.name || 'activity'}_${detailActivity.id}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('XML文件下载成功');
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '活动名称', dataIndex: 'name', key: 'name', width: 180, ellipsis: true },
    {
      title: '活动类型', dataIndex: 'type', key: 'type', width: 120,
      render: (type, record) => (
        <Tag color="blue">{record.type_name || type}</Tag>
      )
    },
    {
      title: '活动时间', key: 'time', width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined /> 开始: {new Date(record.begin_time).toLocaleString('zh-CN')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined /> 结束: {new Date(record.end_time).toLocaleString('zh-CN')}
          </Text>
        </Space>
      )
    },
    {
      title: '状态', key: 'status', width: 100,
      render: (_, record) => {
        const actStatus = getActivityStatus(record);
        const statusInfo = ACTIVITY_STATUS_MAP[actStatus];
        return <Badge status={statusInfo.color} text={statusInfo.text} />;
      }
    },
    {
      title: '审核', dataIndex: 'status', key: 'audit_status', width: 80,
      render: (status) => <Tag color={STATUS_MAP[status]?.color}>{STATUS_MAP[status]?.text}</Tag>
    },
    {
      title: '启用', dataIndex: 'enable', key: 'enable', width: 70,
      render: (enable) => enable ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>
    },
    {
      title: '推荐', dataIndex: 'recommend', key: 'recommend', width: 70,
      render: (recommend) => recommend ? <Tag color="gold">是</Tag> : <Tag>否</Tag>
    },
    {
      title: '排序', dataIndex: 'ind', key: 'ind', width: 70
    },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>详情</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此活动？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={<span><CalendarOutlined /> 活动管理</span>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => loadActivities()}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建活动</Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="搜索活动名称或ID"
            onSearch={(v) => { setFilters({ keyword: v }); loadActivities({ keyword: v }); }}
            style={{ width: 200 }}
          />
          <Select
            placeholder="活动类型"
            allowClear
            style={{ width: 150 }}
            onChange={(v) => { setFilters({ type: v }); loadActivities({ type: v }); }}
          >
            {activityTypes.map(type => (
              <Option key={type.id} value={type.id}>{type.name}</Option>
            ))}
          </Select>
          <Select
            placeholder="审核状态"
            allowClear
            style={{ width: 120 }}
            onChange={(v) => { setFilters({ status: v }); loadActivities({ status: v }); }}
          >
            {Object.entries(STATUS_MAP).map(([key, val]) => (
              <Option key={key} value={parseInt(key)}>{val.text}</Option>
            ))}
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={activities}
          loading={loading}
          pagination={{ ...pagination, onChange: (p) => setPagination(prev => ({ ...prev, current: p })) }}
          rowKey="id"
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingActivity ? '编辑活动' : '新建活动'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={1200}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input placeholder="请输入活动名称" />
          </Form.Item>
          
          <Form.Item name="type" label="活动类型" rules={[{ required: true, message: '请选择活动类型' }]}>
            <Select placeholder="请选择活动类型" onChange={handleTypeChange}>
              {activityTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="time_range" label="活动时间" rules={[{ required: true, message: '请选择活动时间' }]}>
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="status" label="审核状态">
            <Select>
              <Option value={0}>草稿</Option>
              <Option value={1}>已审核</Option>
              <Option value={2}>已拒绝</Option>
            </Select>
          </Form.Item>

          <Form.Item name="ind" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="preview_time" label="预览时间(秒)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="活动开始前预览时间" />
          </Form.Item>

          <Form.Item name="endview_time" label="结束展示时间(秒)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="活动结束后展示时间" />
          </Form.Item>

          <Form.Item name="recommend_time_range" label="推荐时间">
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          {editableXmlData.sections.length > 0 ? (
            <Form.Item label="XML配置编辑">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space style={{ marginBottom: 8 }}>
                  <Button 
                    icon={<UploadOutlined />} 
                    onClick={() => fileInputRef.current?.click()}
                  >
                    上传XML文件
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xml"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        setXmlContent(text);
                        setEditableXmlData(parseXmlToEditable(text));
                        message.success('XML文件加载成功');
                      } catch {
                        message.error('XML文件读取失败');
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button onClick={() => {
                    setXmlContent('');
                    setEditableXmlData({ sections: [], xmlString: '' });
                  }}>清空</Button>
                </Space>
                {editableXmlData.sections.map((section, sectionIdx) => {
                  // 获取当前活动类型的字段映射
                  const activityType = editingActivity?.type || form.getFieldValue('type');
                  const typeFieldMap = ACTIVITY_TYPE_FIELD_MAPS[activityType];
                  const sectionFieldMap = typeFieldMap?.[section.originalName || section.name];
                  
                  return (
                    <Card key={sectionIdx} size="small" title={`${section.name} (${section.rows.length}条)`} style={{ marginBottom: 16 }}>
                      <Table
                        columns={Object.keys(section.rows[0] || {})
                          .filter(key => key !== 'key')
                          .map(key => ({
                            title: sectionFieldMap?.[key] || FIELD_NAME_MAP[key] || key,
                            dataIndex: key,
                            key: key,
                            ellipsis: true,
                            width: key === 'id' || key === 'type' || key === 'requirement' ? 100 : undefined,
                            render: (text, record, index) => (
                              <Input
                                size="small"
                                value={text || ''}
                                onChange={(e) => {
                                  const newSections = [...editableXmlData.sections];
                                  newSections[sectionIdx].rows[index][key] = e.target.value;
                                  setEditableXmlData({
                                    ...editableXmlData,
                                    sections: newSections
                                  });
                                }}
                              />
                            )
                          }))}
                        dataSource={section.rows}
                        pagination={false}
                        size="small"
                        bordered
                        scroll={{ x: true, y: 400 }}
                      />
                    </Card>
                  );
                })}
              </Space>
            </Form.Item>
          ) : (xmlContent && xmlContent.trim()) ? (
            <Form.Item label="XML配置">
              <Alert 
                message="该活动类型的XML格式暂不支持表格化编辑" 
                description="请直接在下方编辑XML文本"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Button 
                    icon={<UploadOutlined />} 
                    onClick={() => fileInputRef.current?.click()}
                  >
                    上传XML文件
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xml"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        setXmlContent(text);
                        setEditableXmlData(parseXmlToEditable(text));
                        message.success('XML文件加载成功');
                      } catch {
                        message.error('XML文件读取失败');
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button onClick={() => {
                    setXmlContent('');
                    setEditableXmlData({ sections: [], xmlString: '' });
                  }}>清空</Button>
                </Space>
                <TextArea 
                  rows={15} 
                  value={xmlContent}
                  onChange={(e) => setXmlContent(e.target.value)}
                  placeholder="请输入XML配置数据"
                  style={{ fontFamily: 'monospace' }}
                />
              </Space>
            </Form.Item>
          ) : (
            <Form.Item label="XML配置数据">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Button 
                    icon={<UploadOutlined />} 
                    onClick={() => fileInputRef.current?.click()}
                  >
                    上传XML文件
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xml"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        setXmlContent(text);
                        setEditableXmlData(parseXmlToEditable(text));
                        message.success('XML文件加载成功');
                      } catch {
                        message.error('XML文件读取失败');
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button onClick={() => {
                    setXmlContent('');
                    setEditableXmlData({ sections: [], xmlString: '' });
                  }}>清空</Button>
                </Space>
                <TextArea 
                  rows={10} 
                  value={xmlContent}
                  onChange={(e) => setXmlContent(e.target.value)}
                  placeholder="请输入或上传XML配置数据"
                  style={{ fontFamily: 'monospace' }}
                />
              </Space>
            </Form.Item>
          )}

          <Form.Item label="其他设置">
            <Space direction="vertical">
              <Form.Item name="enable" valuePropName="checked" noStyle>
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
              <Text> 活动启用</Text>
            </Space>
            <Space direction="vertical" style={{ marginLeft: 24 }}>
              <Form.Item name="visible" valuePropName="checked" noStyle>
                <Switch checkedChildren="可见" unCheckedChildren="隐藏" />
              </Form.Item>
              <Text> 活动可见</Text>
            </Space>
            <Space direction="vertical" style={{ marginLeft: 24 }}>
              <Form.Item name="is_notice" valuePropName="checked" noStyle>
                <Switch checkedChildren="通知" unCheckedChildren="不通知" />
              </Form.Item>
              <Text> 系统通知</Text>
            </Space>
            <Space direction="vertical" style={{ marginLeft: 24 }}>
              <Form.Item name="recommend" valuePropName="checked" noStyle>
                <Switch checkedChildren="推荐" unCheckedChildren="不推荐" />
              </Form.Item>
              <Text> 推荐活动</Text>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="活动详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={1000}
      >
        {detailActivity && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="活动ID">{detailActivity.id}</Descriptions.Item>
              <Descriptions.Item label="活动名称">{detailActivity.name}</Descriptions.Item>
              <Descriptions.Item label="活动类型">
                <Tag color="blue">{detailActivity.type_name || detailActivity.type}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="审核状态">
                <Tag color={STATUS_MAP[detailActivity.status]?.color}>
                  {STATUS_MAP[detailActivity.status]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="开始时间">
                {new Date(detailActivity.begin_time).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="结束时间">
                {new Date(detailActivity.end_time).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="启用">
                {detailActivity.enable ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="可见">
                {detailActivity.visible ? <Tag color="green">是</Tag> : <Tag>否</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="推荐">
                {detailActivity.recommend ? <Tag color="gold">是</Tag> : <Tag>否</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="排序">{detailActivity.ind}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">物品配置详情</Divider>
            
            {parseXmlItems(detailActivity.xml_data, detailActivity.type).length > 0 ? (
              <Table
                columns={[
                  { title: '所属节点', dataIndex: 'section', key: 'section', width: 120 },
                  { title: '节点ID', dataIndex: 'node_id', key: 'node_id', width: 80 },
                  { title: '属性名', dataIndex: 'attr_name', key: 'attr_name', width: 120 },
                  { title: '物品ID', dataIndex: 'item_id', key: 'item_id', width: 100,
                    render: (text) => <Tag color="blue">{text}</Tag>
                  },
                  { title: '数量', dataIndex: 'count', key: 'count', width: 80,
                    render: (text) => <Tag color="green">{text}</Tag>
                  },
                  { title: '完整值', dataIndex: 'full_value', key: 'full_value', ellipsis: true }
                ]}
                dataSource={parseXmlItems(detailActivity.xml_data, detailActivity.type)}
                pagination={false}
                size="small"
                bordered
              />
            ) : (
              <Card size="small">
                <Text type="secondary">未找到物品配置信息</Text>
              </Card>
            )}

            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadXml}>
                下载XML文件
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ActivityManagement;
